/**
 * The one call behind the duplicate button: POST /projects/:id/copy.
 *
 * Its own file rather than another export in `lib/projects.ts`, which is at the
 * eight hundred lines AGENTS.md §4 calls the point where the next feature goes
 * somewhere else, and which already describes the register, the summary, the
 * milestones, the time entries, the expenses and the contracts. Copying shares
 * nothing with any of them but the `Project` type, which is imported.
 *
 * WHAT A COPY CONTAINS IS NOT DECIDED HERE, and must not be. The server file
 * `crm-projects/lib/projectCopy.ts` decides it column by column with a reason
 * per line, and the dialog in front of this call promises the operator what that
 * file does. A second opinion on this side - a default filled in here, a field
 * stripped before sending - would be a promise made by code that does not do the
 * work, and it would drift the first time the rule changes on the server.
 *
 * The switches are therefore REQUIRED in the type below, even though the route
 * defaults each of them to false. A caller that forgets one would get the
 * server's default silently; a caller that cannot compile until it says what it
 * wants has to have looked at the dialog's boxes. The dialog is the only caller
 * today and it always sends all four.
 */
import { api } from "./api.js";
import type { Project } from "./projects.js";

/** What the operator asked the copy to carry. `name` is the dialog's field. */
export interface ProjectCopyOptions {
  name: string;
  tasks: boolean;
  milestones: boolean;
  team: boolean;
  /** Carry the project's flow map over. The server points its milestone steps
   *  at the copied milestones, drops its task pointers and clears every tick. */
  flowMap: boolean;
}

/**
 * Duplicate a project and answer with the new one.
 *
 * The response is the full project row, in the same shape the register's list
 * and the detail page read, so a caller can navigate straight into it without a
 * second GET. That matters here more than usual: the copy is a record the
 * operator has never seen, and the reason to hand back the whole row is so the
 * screen can show it rather than announce that something happened somewhere.
 *
 * Failures come back as `ApiError` and are left to the caller. The named one
 * worth knowing about is a 400 `copy_too_large`, which the route sends when the
 * source is over 500 tasks or 100 milestones instead of opening a transaction it
 * would time out of, and which carries both counts and both limits in the body.
 * Production's largest project holds a fraction of either, so nothing reaches it
 * today - the dialog reads it anyway (`tooLargeCounts` in
 * `DuplicateProjectModal.tsx`), because the alternative is writing the sentence
 * on the day somebody reports the button as broken.
 */
export const copyProject = (projectId: string, opts: ProjectCopyOptions) =>
  api.post<Project>(`/admin/crm/projects/${projectId}/copy`, opts);
